import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { removeCurrentCrmQueryCache } from '@/lib/persistedCrmQueryCache';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [isReady, setIsReady] = useState(false);
  const [temporarilyUnavailable, setTemporarilyUnavailable] = useState(false);
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  
  useEffect(() => {
    let active = true;

    // In development mode, always allow access without authentication
    if (process.env.NODE_ENV === 'development') {
      setIsReady(true);
    } else {
      const token = localStorage.getItem('auth_token');
      fetch('/api/auth/me', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then(async (response) => {
        if (!active) return;
        if (response.ok) {
          // ProtectedRoute ha appena eseguito la verifica autorevole: riusa la
          // stessa risposta nel QueryClient invece di avviare una seconda
          // /api/auth/me prima che il Feed possa leggere la cache owner-scoped.
          const payload = await response.json();
          if (!active) return;
          queryClient.setQueryData(['/api/auth/me'], payload);
          setTemporarilyUnavailable(false);
          setIsReady(true);
          return;
        }
        // Soltanto un rifiuto di autenticazione prova che la sessione non e'
        // valida. Un 5xx/503 e' un guasto temporaneo del backend: cancellare il
        // token in quel caso trasformava un rallentamento DB in un falso logout.
        if (response.status === 401 || response.status === 403) {
          removeCurrentCrmQueryCache();
          localStorage.removeItem('auth_token');
          setLocation('/login');
          return;
        }
        setTemporarilyUnavailable(true);
      }).catch(() => {
        if (active) setTemporarilyUnavailable(true);
      });
    }

    return () => { active = false; };
  }, [queryClient, setLocation, verificationAttempt]);

  if (temporarilyUnavailable) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-base text-foreground">Il CRM è temporaneamente non disponibile.</p>
        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          onClick={() => {
            setTemporarilyUnavailable(false);
            setVerificationAttempt((attempt) => attempt + 1);
          }}
        >
          Riprova
        </button>
      </div>
    );
  }
  
  if (!isReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  return <>{children}</>;
}
